import { useQuery } from "react-query";
import { useEffect, useRef, useState } from "react";

import { $ok, $totype, $UUID } from "foundation-ts/commons";
import { TSDate } from "foundation-ts/tsdate";
import { Resp } from "foundation-ts/tsrequest";
import { Nullable } from "foundation-ts/types";

import apiClient from "@services/apiClient";

import { InfosAfterSignupID } from "./hooksConstants";
import { RegisteredInformationDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 10000;
type POLL_STATUS = 'not_started' | 'fetching' | 'resolved' | 'timeout';

const useFetchRegisteredInformation = (corporationId: Nullable<string>) => {
    const [remainingSeconds, setRemainingSeconds] = useState(POLL_TIMEOUT_MS / 1000);
    const [isTimedOut, setIsTimedOut] = useState(false);
    const pollStartTime = useRef<number|null>(null);
    const pollStatus = useRef<POLL_STATUS>("not_started");

    const query = useQuery(
        [InfosAfterSignupID, corporationId],
        async () => {
            pollStatus.current = 'fetching';
            const [resp, status] = await apiClient.getRegisteredInformation($UUID(corporationId)!);
            if (status !== Resp.OK) { return null; }
            const data = $totype<RegisteredInformationDto>(resp);
            if ($ok(data?.corporation.initialFormData?.paymentStatus)) { pollStatus.current = 'resolved'; }
            return data;
        },
        {
            enabled: Boolean(corporationId),
            cacheTime: 0,
            staleTime: 0,
            refetchInterval: (data) => {
                if ($ok(data?.corporation.initialFormData?.paymentStatus)) { 
                    pollStatus.current = 'resolved';
                    return false;
                }

                if (isTimedOut) { return false; }

                return POLL_INTERVAL_MS;
            },
            refetchIntervalInBackground: false
        }
    );

    useEffect(() => {
        if (!$ok(corporationId)) { return; }

        pollStartTime.current = TSDate.zulu().timestamp;
        setRemainingSeconds(POLL_TIMEOUT_MS / 1000);
        setIsTimedOut(false);

        const timer = setInterval(() => {
            if (pollStatus.current === "resolved" || pollStatus.current === "timeout") {
                clearInterval(timer);
                return;
            }

            const elapsed = TSDate.zulu().timestamp - pollStartTime.current!;
            const remaining = Math.max(0, POLL_TIMEOUT_MS/1000 - elapsed);
            setRemainingSeconds(remaining);

            if (remaining === 0) {
                pollStatus.current = "timeout";
                clearInterval(timer);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [corporationId]);

    return {
        ...query,
        remainingSeconds,
        pollStatus: pollStatus.current,
    };
};

export default useFetchRegisteredInformation ;